import React from 'react';
import { useSelector } from 'react-redux';
import {
  AppContent,
  Ad,
  ErrorBoundary,
  HeaderWithHighlights,
  HeaderWithSixSixArticleList,
} from 'mm-ui-components';
import { Store } from 'redux';
import { Theme } from '../../components/connectedComponents/Theme';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { SECTION_NAMES, SLOT_NAMES } from '../templates.const';
import {
  LoadMoreButton,
  useFetchNextPosts,
} from '../../components/connectedComponents/LoadMoreButton';
import { Page } from '../Page';
import { getSectionArticlesByKey, getSectionDisplayNameByKey, getSectionNextByKey, getSectionTextByKey } from '../../store/template/feedPage/feedpage.selectors';
import { HeaderConnected } from '../../components/connectedComponents/HeaderConnected';
import { FooterConnected } from '../../components/connectedComponents/FooterConnected';
import { MainWrapperConnected } from '../../components/connectedComponents/MainWrapperConnected';
import { getAdSlotId } from '../../store/config/config.selectors';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppState, AppActions } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';

interface FeedPageProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

const getArticlesInTemplate = (state: AppState) => {
  const topSectionArticles = getSectionArticlesByKey('topSection')(state) || [];
  const feedSectionArticles = getSectionArticlesByKey('feedSection')(state) || [];
  return [
    ...topSectionArticles,
    ...feedSectionArticles,
  ];
};

export const FeedPageComponent: React.FunctionComponent<FeedPageProps> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <FeedPageContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

export const FeedPageContentConnected: React.FunctionComponent = () => {
  const topSectionDisplayName = useSelector(getSectionDisplayNameByKey(SECTION_NAMES.TOP_SECTION));
  const topSectionArticles = useSelector(getSectionArticlesByKey(SECTION_NAMES.TOP_SECTION));
  const cardsSectionDisplayName = useSelector(getSectionDisplayNameByKey(SECTION_NAMES.FEED_SECTION));
  const cardsSectionArticles = useSelector(getSectionArticlesByKey(SECTION_NAMES.FEED_SECTION));
  const articles = useSelector(getArticlesInTemplate);
  const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
  const oneOnOneId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));

  return (
    <React.Fragment>
      <MetaTags articles={articles} />
      <HeadScripts />
      <Theme>
        <AppContent>
          <HeaderConnected />
          <MainWrapperConnected>
            <Ad id={topAdId} />
            <HeaderWithHighlights title={topSectionDisplayName} cardsProps={topSectionArticles} />
            <HeaderWithSixSixArticleList title={cardsSectionDisplayName} cardsProps={cardsSectionArticles} />
            <LoadMoreFeedPostsButtonConnected />
            <Ad id={oneOnOneId} />
          </MainWrapperConnected>
          <FooterConnected />
        </AppContent>
      </Theme>
    </React.Fragment>
  );
};

export const LoadMoreFeedPostsButtonConnected: React.FunctionComponent = () => {
  const sectionKey = SECTION_NAMES.FEED_SECTION;
  const shouldDisplay = !!useSelector(getSectionNextByKey(sectionKey));
  const text = useSelector(getSectionTextByKey(sectionKey));
  const onClick = useFetchNextPosts(sectionKey, getSectionNextByKey);
  return <LoadMoreButton {...{ onClick, shouldDisplay, text }} />;
};
